import React from 'react';
import Link from 'next/link';
import { useQuery } from '@tanstack/react-query';
import { Cormorant_Garamond } from 'next/font/google';
import { applicationsService, GET_RETAINER_INFO } from '@/api';
import { RetainerBody, RetainerMassActionBody } from '@/components/screens';
import { RetainerSecurityBody } from '@/components/screens/retainer/security/RetainerSecurityBody';
import { LoaderScreen, Typography, ZoomControls } from '@/components/common';
import { HeaderNote } from '../HeaderNote';
import { FormRetainer } from './FormRetainer';
import { CaseType, ItemDocumentPackage } from '@/interfaces';
import { RetainerHeader } from '@/components/screens/retainer/RetainerHeader';

export const cormorant = Cormorant_Garamond({
	subsets: ['latin'],
	weight: ['400'],
	style: ['normal'],
	display: 'swap',
	preload: true,
	variable: '--font-camorant',
});

interface Props {
	hasNextDocument: boolean;
	document: ItemDocumentPackage;
	onContinue: (isApproved: boolean) => void;
	isClientAction?: boolean;
	caseType?: CaseType;
}

export const RetainerDocument: React.FC<Props> = ({
	document,
	hasNextDocument,
	onContinue,
	isClientAction,
	caseType,
}) => {
	const containerRef = React.useRef<HTMLDivElement>(null);

	const { data: retainer, isLoading } = useQuery({
		queryKey: [GET_RETAINER_INFO, document.applicationId],
		queryFn: () =>
			applicationsService.getRetainerInfo(Number(document.applicationId)),
	});

	const caseName = retainer?.data.caseName ?? '';
	const categoryInfo = retainer?.data.categoryName;
	const templateInfo = retainer?.data.templateInfo;
	const retainerInfo = retainer?.data.retainerInfo;

	const name = templateInfo?.name ?? '';
	const firmName = templateInfo?.firmName ?? '';
	const fullName = templateInfo?.fullName ?? '';
	const companyName = templateInfo?.companyName ?? '';
	const companyAlias = templateInfo?.companyAlias ?? '';
	const requiredDocuments = templateInfo?.requiredDocuments ?? '';
	const eligibilityCriteria = templateInfo?.eligibilityCriteria ?? '';
	const lawyerSign = retainerInfo?.lawyerSign ?? '';
	const userSign = retainerInfo?.userSign ?? '';
	const signedAt = retainerInfo?.signedAt ?? '';

	const optionalLocation = templateInfo?.optionalLocation
		? templateInfo.optionalLocation?.replace(
				'Optional depending on location of Law  Firm:',
				''
		  )
		: '';

	return (
		<section className="flex flex-col md:flex-row gap-6 md:h-full">
			{isLoading ? (
				<LoaderScreen />
			) : (
				<div className="flex-1 md:h-full">
					<ZoomControls
						height="73vh"
						className="md:!h-full"
						scrollRef={containerRef}
					>
						<div className="bg-white border border-[#0D061614] shadow-shadow2 rounded-lg px-4 lg:px-10">
							<RetainerHeader caseName={caseName} category={categoryInfo!} />
							<div className="relative m-auto text-left">
								{caseType === 'MASS_ACTION' ? (
									<RetainerMassActionBody name={name} fullName={fullName} />
								) : categoryInfo?.toLowerCase() === 'securities' ? (
									<RetainerSecurityBody name={name} fullName={fullName} />
								) : (
									<RetainerBody
										name={name}
										firmName={firmName}
										fullName={fullName}
										companyName={companyName}
										companyAlias={companyAlias}
										requiredDocuments={requiredDocuments}
										eligibilityCriteria={eligibilityCriteria}
									/>
								)}
								<div className="flex">
									<div className="flex-1 hidden md:block" />
									<div className="flex-1 flex flex-col">
										<div className="space-y-4">
											<Typography
												type="regular-18"
												className="text-right md:text-left"
											>
												Best,
											</Typography>
										</div>
										{lawyerSign ? (
											<p className="text-4xl font-praise mt-2 text-right md:text-left">
												{lawyerSign}
											</p>
										) : null}
									</div>
								</div>
								<div className="flex justify-center mt-3 mb-3">
									<Link href={'https://lowey.com/'} target="_blank">
										<Typography
											type="regular-18"
											className={`!text-[#236A96] ${cormorant.className}`}
										>
											www.lowey.com
										</Typography>
									</Link>
								</div>
							</div>
						</div>
					</ZoomControls>
				</div>
			)}

			<div
				className={
					isClientAction
						? 'xl:w-[35%] shrink-0 space-y-4 flex flex-col'
						: 'xl:w-[23%] shrink-0 space-y-4 flex flex-col'
				}
			>
				<HeaderNote showNote={false} />
				<FormRetainer
					actionRequiredId={String(document.id)}
					applicationId={document.applicationId}
					name={name}
					caseName={caseName}
					categoryInfo={categoryInfo}
					firmName={firmName}
					fullName={fullName}
					companyName={companyName}
					companyAlias={companyAlias}
					requiredDocuments={requiredDocuments}
					eligibilityCriteria={eligibilityCriteria}
					lawyerSign={lawyerSign}
					optionalLocation={optionalLocation}
					hasNextDocument={hasNextDocument}
					onContinue={onContinue}
					userSign={userSign}
					signedAt={signedAt}
					caseType={caseType}
				/>
			</div>
		</section>
	);
};
